@use '../../../common/styles/variables' as *;
@use '../../../common/mixins/inputs' as *;

:host {
  display: block;
  height: 100%;
  @include default-css-variables;
}

.nylas-event-calendar {
  height: inherit;
  padding: 0 16px;
}

.calendar-header {
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  z-index: 3;
  background: var(--nylas-base-0);

  .calendar-header__row_one {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid var(--nylas-base-200);
  }

  .calendar-header__row_two {
    display: flex;
    justify-content: space-between;
    margin: 12px 0;
    justify-self: center;
  }

  .calendar-header__title {
    display: flex;
    align-items: center;
    gap: 16px;
    font-family: var(--nylas-font-family);
    font-size: 20px;
    font-weight: 600;
    line-height: 30px;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
    color: var(--nylas-base-900);

    .calendar-header-prev-next {
      display: flex;
      align-items: center;

      .chevron-right {
        transform: rotate(180deg);
      }

      .button {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        height: 3vh;
        width: 3vh;
        min-width: 3em;
        min-height: 3em;
        cursor: pointer;
        border-radius: var(--nylas-border-radius-2x);

        background-color: transparent;
        color: var(--nylas-base-800);
        cursor: pointer;

        &:hover {
          background-color: var(--nylas-base-50);
        }

        border: none;

        &:active {
          background-color: var(--nylas-base-100);
        }

        &:disabled {
          background-color: transparent;
          color: var(--nylas-base-300);
          cursor: not-allowed;
        }
      }
    }
  }

  .calendar-header__current-calendar {
    padding: 6px 12px;
    border-radius: var(--nylas-border-radius);
    border: 1px solid var(--nylas-base-200);
    color: var(--nylas-base-900);
  }

  .calendar-header__hide-non-group-events {
    margin-right: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    color: var(--nylas-base-900);
    label {
      display: flex;
      align-items: center;
      gap: 4px;
    }
    tooltip-component {
      &::part(tc__content) {
        left: 24px;
      }
    }
  }

  .calendar-header__view-options {
    @media #{$mobile} {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      width: -webkit-fill-available;
    }

    button {
      color: var(--nylas-base-800);
      background: var(--nylas-base-0);

      &.selected {
        background-color: var(--nylas-color-blue-100);
        border-radius: 4px;
      }

      &:hover:not(.selected) {
        background: var(--nylas-base-50);
      }
    }
  }

  @media #{$mobile} {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 16px;
  }
}

.calendar-header h3 {
  font-size: 1.5rem;
  font-weight: 600;
}

button {
  padding: 0.5rem 1rem;
  margin-right: 0.5rem;
  border: none;
  background-color: #0073e6;
  color: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
}

button:hover {
  background-color: #005bb5;
}

.calendar-container {
  border-radius: 4px;
  flex: 1;
  display: flex;
  flex-direction: column;
  height: calc(100% - 16px);
  min-height: 0;
  position: relative;
  overflow-y: auto;
  z-index: 0;

  // FullCalendar CSS custom properties should be declared before any nested rules
  --fc-today-bg-color: var(--nylas-base-0);
  --fc-now-indicator-color: var(--nylas-error);

  @media #{$mobile} {
    height: calc(100% - 116px);
  }

  &.show-event {
    height: calc(
      100% - 456px - 80px - 16px
    ); // 456px is the max-height of the event details, 80px is the height of the footer and 16px is the margin between the event details and the calendar

    @media #{$mobile} {
      height: calc(100% - 116px + 16px);
    }
  }

  /* Allow proper shrinking within flexbox */
  &.hide {
    display: none;
  }

  

  // Full calendar
  .fc .fc-view-harness {
    .fc-scrollgrid {
      border-right-width: 0px;
      position: relative;
      border-top: 0;

      thead {
        position: sticky !important;
        top: 96px;
        z-index: 2;
      }
      tbody {
        .fc-timegrid-divider {
          display: none;
        }
      }
    }

    .fc-col-header {
      position: sticky;
      top: 0;
      background: var(--nylas-base-0);
      border-top: 1px solid var(--nylas-base-200);
    }

    /* General Header Styling */
    .custom-day-header {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      font-family: Arial, sans-serif;
      font-size: 14px;
      color: #1e3a8a;
      /* Navy Blue */
      font-weight: bold;
      text-transform: uppercase;
    }

    /* Styling for the Day Name (e.g., SUN, MON) */
    .custom-day-header .day-name {
      color: var(--nylas-base-400);
      margin-bottom: 4px;
      font-family: var(--nylas-font-family);
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      text-align: left;
      text-underline-position: from-font;
      text-decoration-skip-ink: none;
    }

    /* Styling for the Day Number (e.g., 31, 01) */
    .custom-day-header .day-number {
      font-size: 16px;
      background-color: var(--nylas-base-0);
      color: var(--nylas-base-800);
      padding: 6px;
      border-radius: 50%;
      font-family: var(--nylas-font-family);
      font-size: 22px;
      font-weight: 500;
      line-height: 32px;
      text-align: left;
      text-underline-position: from-font;
      text-decoration-skip-ink: none;
      width: 44px;
      display: inline-flex;
      justify-content: center;
      align-items: center;

      &.today {
        background-color: var(--nylas-primary);
        color: var(--nylas-base-0);
      }
    }
  }

  .fc .fc-timegrid-now-indicator-line {
    border-width: 1.5px 0 0;
  }

  .fc .fc-timegrid-slot-label {
    color: var(--nylas-base-900);
  }

  .fc .fc-daygrid-day.fc-day-today {
    background-color: transparent;
  }

  .fc .fc-day-today {
    .fc-daygrid-day-top {
      .fc-daygrid-day-number {
        background-color: var(--nylas-primary);
        color: var(--nylas-base-0);
        border-radius: 50%;
        width: 36px;
        height: 36px;
        margin: 8px 8px;
      }
    }
  }

  .fc .fc-daygrid-day-top {
    flex-direction: row;

    .fc-daygrid-day-number {
      display: inline-flex;
      justify-content: center;
      align-items: center;
      padding: 16px;
      color: var(--nylas-base-400);
      font-family: var(--nylas-font-family);
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      text-align: left;
      text-underline-position: from-font;
      text-decoration-skip-ink: none;
    }
  }

  .fc .fc-v-event {
    border: none;

    .fc-event-main {
      border-radius: 2px;
    }

    &.group-event {
      .fc-event-main {
        background: var(--nylas-color-green-50);
        color: var(--nylas-color-green-900);
      }
      &:not(.read-only):not(.no-participants) {
        cursor: pointer;
      }
    }

    &.regular-event {
      .fc-event-main {
        background: var(--nylas-color-blue-300);
        color: var(--nylas-base-800);
      }
      &:not(.read-only):not(.no-participants) {
        cursor: pointer;
      }
    }

    &.read-only,
    &.no-participants {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.action-options {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  z-index: 4;

  @media #{$mobile} {
    height: calc(100% - 96px);
  }

  &.update-options,
  &.delete-options,
  &.discard-changes {
    height: calc(100% + 80px);
    border-bottom-left-radius: var(--nylas-border-radius-2x);
    border-bottom-right-radius: var(--nylas-border-radius-2x);

    @media #{$mobile} {
      height: calc(100% + 176px);
    }
  }

  .action-options__card {
    display: flex;
    flex-direction: column;
    justify-content: center;
    background: var(--nylas-base-0);
    border-radius: var(--nylas-border-radius-2x);
    min-width: 240px;

    &.discard-changes__card {
      min-width: 320px;
    }

    button-component#close-edit-options,
    button-component#close-update-options,
    button-component#close-delete-options,
    button-component#close-discard-changes,
    button-component#close-choose-participants-modal {
      display: flex;
      justify-content: flex-end;
      padding-top: 8px;

      button {
        padding: 0;
        height: auto;
        width: auto;

        &:hover {
          background-color: transparent !important;
        }
      }
    }

    .action-options__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 8px;
      border-bottom: 1px solid var(--nylas-base-200);

      h3 {
        font-family: var(--nylas-font-family);
        font-size: 18px;
        font-weight: 500;
        line-height: 30px;
        text-align: left;
        text-underline-position: from-font;
        text-decoration-skip-ink: none;
        color: var(--nylas-base-900);
        margin: 0;
        padding: 8px 16px 0;
      }
    }

    .edit-options__content {
      ul li#import {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
    }

    .action-options__content {
      &.choose-participants-modal__content {
        padding: 24px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        width: 400px;
        max-height: 200px;
        overflow-y: auto;

        @media #{$mobile} {
          width: -webkit-fill-available;
          max-height: 120px;
        }
      }

      &.discard-changes__content {
        padding: 24px;

        h3 {
          font-family: var(--nylas-font-family);
          font-size: 20px;
          font-weight: 500;
          line-height: 30px;
          text-align: left;
          text-underline-position: from-font;
          text-decoration-skip-ink: none;
          color: var(--nylas-base-900);
          margin: 0;
        }
      }

      ul {
        padding: 0;
        list-style-type: none;
        color: var(--nylas-base-900);
        max-height: 336px;

        li {
          padding: 16px, 12px, 16px, 12px;
          color: var(--nylas-base-900);
          padding: 12px 16px;
          text-decoration: none;
          display: block;
          font-family: var(--nylas-font-family);
          font-weight: 400;
          font-size: 14px;
          line-height: 21px;
          letter-spacing: 0px;
          text-align: left;
          cursor: pointer;

          &:hover,
          &:focus {
            background-color: var(--nylas-base-100);
          }

          &.disabled {
            cursor: not-allowed;
            opacity: 0.5;
          }
        }
      }
    }

    .action-options__footer {
      display: flex;
      justify-content: flex-end;
      gap: 16px;
      padding: 16px;
      border-top: 1px solid var(--nylas-base-200);
    }
  }
}

.event-details-container {
  position: absolute;
  bottom: -80px;
  left: 0;
  width: 100%;
  z-index: 3;
  background: var(--nylas-base-0);
  box-shadow: 4px -8px 12px -12px rgba(0, 0, 0, 0.5);
  border-bottom-left-radius: var(--nylas-border-radius-2x);
  border-bottom-right-radius: var(--nylas-border-radius-2x);

  @media #{$mobile} {
    bottom: -176px;
  }

  .event-details-header {
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 16px;
    border-bottom: 1px solid var(--nylas-base-200);
    color: var(--nylas-base-900);

    div.event-details-header__actions {
      display: flex;
      align-items: center;
      gap: 16px;

      button-component#close {
        button {
          padding: 0;
          height: auto;

          &:hover {
            background-color: transparent !important;
          }
        }
      }
    }
  }

  .event-details {
    max-height: 440px;
    overflow-y: scroll;
    padding: 0 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 16px;

    nylas-additional-participants::part(nadtnlpart__content) {
      background-color: var(--nylas-base-0);
    }

    &.edit-event-details {
      nylas-additional-participants {
        margin: 16px 0;

        &::part(nfc) {
          border: none;
        }

        &::part(nadtnlpart__title) {
          font-weight: 400;
        }

        &::part(nadtnlpart__content) {
          padding: 16px 0;
        }

        &::part(nfc__header) {
          padding: 0;
          border-bottom: none;
        }
      }
    }

    .event-details-form-inputs {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
  }

  .event-details-footer {
    display: flex;
    justify-content: flex-end;
    gap: 16px;
    padding: 16px;
    border-top: 1px solid var(--nylas-base-200);

    .event-details-footer__error {
      color: var(--nylas-error);
      font-family: var(--nylas-font-family);
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      text-align: left;
      text-underline-position: from-font;
    }

    .event-details-footer__helper-text {
      color: var(--nylas-base-800);
      font-family: var(--nylas-font-family);
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      text-align: left;
      text-underline-position: from-font;
    }

    .event-details-footer__actions {
      display: flex;
      gap: 16px;
    }
  }
}
